import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin } from 'lucide-react';
import { api } from '../lib/api';
import advisor from '../lib/advisor';

const TABS = [
  { id: 'venta', label: 'Comprar' },
  { id: 'arriendo', label: 'Arrendar' },
];

export default function SearchBar({ initialTab = 'venta', dark = false }) {
  const [tab, setTab] = useState(initialTab);
  const [q, setQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    if (!showSug) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res = await api.listMunicipalities(q, 6);
        setSuggestions(res.items || []);
      } catch {
        setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(timer.current);
  }, [q, showSug]);

  const submit = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    params.set('op', tab);
    if (q) params.set('q', q);
    // Advisor: iniciar/enriquecer Context al comenzar una búsqueda.
    // Fire-and-forget: nunca bloquea la navegación.
    advisor.getOrStartContext({
      entryPoint: 'search',
      intent: q ? `buscar ${tab} en ${q}` : `buscar ${tab}`,
      payload: { op: tab, q: q || null, started_from: 'searchbar' },
    }).then(() => {
      // Si ya existía un Context activo, agregamos el patch de la nueva búsqueda.
      advisor.updateContext('search_submitted', { op: tab, q: q || null });
    });
    navigate(`/buscar?${params.toString()}`);
  };

  return (
    <div className="w-full max-w-3xl" data-testid="hero-search-bar">
      <div className={`inline-flex items-center gap-1 p-1 rounded-full mb-3 ${dark ? 'bg-white/10 backdrop-blur' : 'bg-white/70 backdrop-blur shadow-sm'}`}>
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`hero-tab-${t.id}`}
              className={`px-6 h-9 rounded-full text-sm font-semibold transition-all ${active ? 'bg-habita text-white shadow-sm' : (dark ? 'text-white/80 hover:text-white' : 'text-gray-700 hover:text-habita')}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-2 flex items-center gap-2 relative">
        <div className="flex items-center gap-2 flex-1 px-4 py-2 min-w-0">
          <MapPin className="w-5 h-5 text-habita shrink-0" />
          <input
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onBlur={() => setTimeout(() => setShowSug(false), 150)}
            placeholder="Busca por municipio, barrio o departamento"
            className="flex-1 outline-none text-base text-gray-900 placeholder:text-gray-400 bg-transparent min-w-0"
            data-testid="hero-search-input"
          />
        </div>
        <button type="submit" className="btn-habita h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 shrink-0" data-testid="hero-search-submit">
          <Search className="w-4 h-4" /> Buscar
        </button>

        {showSug && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-20">
            {suggestions.map((m) => (
              <button
                key={m.code || `${m.name}-${m.dept}`}
                type="button"
                onMouseDown={() => { setQ(m.name); setShowSug(false); }}
                className="w-full text-left px-5 py-3 hover:bg-habita-mint flex items-center gap-3 text-sm"
                data-testid={`hero-sug-${m.code || m.name}`}
              >
                <MapPin className="w-4 h-4 text-gray-400" />
                <span className="flex-1 min-w-0">
                  <span className="text-habita-dark font-medium">{m.name}</span>
                  <span className="text-xs text-gray-500 ml-2">{m.dept}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </form>
    </div>
  );
}
